import * as Yup from "yup";
import classNames from "classnames";
import { Auth } from 'aws-amplify';
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { Formik, Field, Form } from "formik";
import { Modal } from "../../../molecules";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { API_RESPONSES, EMPTY_STRING, REGEX } from "../../../../constants";
import { MODAL_SIZE } from "../../AppModals/modals.constants";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { showPasswordIcon, hidePasswordIcon } from "../../../../assets/icons";
import { Alert } from "../../../molecules";
import { actions as spinnerActions } from "../../Spinner/spinner.reducers"
import { isEmpty } from "lodash";
import { VALIDATION_MESSAGES } from "../../../../constants";

const { addSpinner, removeSpinner } = spinnerActions;
const { setSelectedModal } = modalActions;
const { MD } = MODAL_SIZE;
const { UPDATE_USER_PASSWORD } = SPINNER_NAMES;
const {ERROR, SUCCESS} = API_RESPONSES;
const SUCCESSMESSAGE = "Password Successfully Updated.";
const { CURRENT_PASSWORD_REQUIRED, FIELD_EMPTY, MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH, PASSWORD_SHOULD_CONTAINS, 
  NEW_OLD_SHOULD_NOT_BE_SAME, VALIDATE_PASSWORD_SYMBOL, CONFIRM_PASSWORD_REQUIRED, MATCH_PASSWORD, NEW_PASS, CURRENT_PASS, 
  VALIDATE_PASSWORD } = VALIDATION_MESSAGES

const UpdatePasswordModal = () => {
  const dispatch = useDispatch();
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [passwordStates, setPasswordStates] = useState({
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });
  
  const [alertData, setAlertData] = useState();
  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
  }, [dispatch]);
  const { PASSWORD } = REGEX;
  const initialValues = {
    currentPassword: EMPTY_STRING,
    newPassword: EMPTY_STRING,
    confirmPassword: EMPTY_STRING,
  };

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required(CURRENT_PASSWORD_REQUIRED),
    newPassword: Yup.string()
      .required(FIELD_EMPTY)
      .min(8, MIN_PASSWORD_LENGTH)
      .max(16, MAX_PASSWORD_LENGTH)
      .test(
        VALIDATE_PASSWORD,
        PASSWORD_SHOULD_CONTAINS,
        (val) => val && PASSWORD.test(val)
      )
      .notOneOf(
        [Yup.ref(CURRENT_PASS)],
        NEW_OLD_SHOULD_NOT_BE_SAME
      )
      .matches(/[^\w]/, VALIDATE_PASSWORD_SYMBOL),
    confirmPassword: Yup.string()
      .required(CONFIRM_PASSWORD_REQUIRED)
      .oneOf([Yup.ref(NEW_PASS)], MATCH_PASSWORD),
  });

  const handleOnSubmit = (values) => {
    dispatch(addSpinner(UPDATE_USER_PASSWORD));
    Auth.currentAuthenticatedUser().then(user => {
      Auth.changePassword(user, values.currentPassword, values.newPassword).then(() => {
        setTimeout(() => {
          handleClose();
        }, "2000");
        setAlertData({ message: SUCCESSMESSAGE, type : SUCCESS })
        dispatch(removeSpinner(UPDATE_USER_PASSWORD));
      }).catch((error) => {
        setAlertData({ message: error.message , type : ERROR});
        dispatch(removeSpinner(UPDATE_USER_PASSWORD));
      });
    }).catch((error) => {
      setAlertData({ message: error.message , type : ERROR});
      dispatch(removeSpinner(UPDATE_USER_PASSWORD));
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ values, handleSubmit, handleReset, touched, errors, isValid }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary"
              type="submit"
              onClick={handleSubmit}
              disabled={!isValid || isSpinnerActive || isEmpty(touched)}
            >
              <Spinner
                name={UPDATE_USER_PASSWORD}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                Submit
              </Spinner>
            </button>
          </div>
        );
        return (
          <Modal
            title="Update Password"
            handleReset={handleReset}
            handleClose={handleClose}
            size={MD}
            footer={footer}
          >
            <Form>
              <div className="p-6">
                <div className="pb-4">
                  <div>
                    {alertData && (
                      <div className="my-2">
                        <Alert
                          type={alertData.type}
                          message={alertData.message}
                          handleOnClose={setAlertData}
                        />
                      </div>
                    )}
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Current Password
                    </label>
                    <div
                      className={classNames(
                        "flex flex-between items-center bg-[#E8F0FE] rounded-md border-2",
                        {
                          "border-red-600":
                            touched.currentPassword && errors.currentPassword,
                        }
                      )}
                    >
                      <Field
                        type={
                          passwordStates.showCurrentPassword
                            ? "text"
                            : "password"
                        }
                        name="currentPassword"
                        placeholder="Current Password"
                        className="form-control !ring-0 border-0"
                        value={values.currentPassword}
                      />
                      <img
                        alt="show Password"
                        src={
                          passwordStates.showCurrentPassword
                            ? showPasswordIcon
                            : hidePasswordIcon
                        }
                        onClick={() =>
                          setPasswordStates({
                            ...passwordStates,
                            showCurrentPassword:
                              !passwordStates.showCurrentPassword,
                          })
                        }
                        className=" w-8 h-4 pr-2 cursor-pointer"
                      />
                    </div>
                    {touched.currentPassword && errors.currentPassword && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.currentPassword}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Enter New Password
                    </label>
                    <div
                      className={classNames(
                        "flex flex-between items-center bg-[#E8F0FE] rounded-md border-2",
                        {
                          "border-red-600":
                            touched.newPassword && errors.newPassword,
                        }
                      )}
                    >
                      <Field
                        type={
                          passwordStates.showNewPassword ? "text" : "password"
                        }
                        className="form-control !ring-0 border-0"
                        placeholder="New Password"
                        name="newPassword"
                        value={values.newPassword}
                        autoComplete="off"
                      />
                      <img
                        alt="show Password"
                        src={
                          passwordStates.showNewPassword
                            ? showPasswordIcon
                            : hidePasswordIcon
                        }
                        onClick={() =>
                          setPasswordStates({
                            ...passwordStates,
                            showNewPassword: !passwordStates.showNewPassword,
                          })
                        }
                        className=" w-8 h-4 pr-2 cursor-pointer"
                      />
                    </div>
                    {touched.newPassword && errors.newPassword && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.newPassword}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Confirm New Password
                    </label>
                    <div
                      className={classNames(
                        "flex flex-between items-center bg-[#E8F0FE] rounded-md border-2",
                        {
                          "border-red-600":
                            touched.confirmPassword && errors.confirmPassword,
                        }
                      )}
                    >
                      <Field
                        type={
                          passwordStates.showConfirmPassword
                            ? "text"
                            : "password"
                        }
                        className="form-control !ring-0 border-0"
                        placeholder="Confirm Password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                      />
                      <img
                        alt="show Password"
                        src={
                          passwordStates.showConfirmPassword
                            ? showPasswordIcon
                            : hidePasswordIcon
                        }
                        onClick={() =>
                          setPasswordStates({
                            ...passwordStates,
                            showConfirmPassword:
                              !passwordStates.showConfirmPassword,
                          })
                        }
                        className=" w-8 h-4 pr-2 cursor-pointer"
                      />
                    </div>
                    {touched.confirmPassword && errors.confirmPassword && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default UpdatePasswordModal;
